import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Receipt, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { invoicesService, type Invoice } from '@/services/invoicesService';
import InvoiceEditor from '@/components/invoicing/InvoiceEditor';
import { INVOICE_STATUS_COLORS, formatCents } from '@/lib/invoiceFormat';
import type { Patient } from '@/lib/types';

interface PatientInvoicesTabProps {
  patient: Patient | null;
}

const PatientInvoicesTab: React.FC<PatientInvoicesTabProps> = ({ patient }) => {
  const { clinic } = useClinic();
  const { toast } = useToast();
  const clinicId = clinic?.id;
  const patientId = patient?.id;

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const fetchInvoices = useCallback(async () => {
    if (!clinicId || !patientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await invoicesService.getPatientInvoices(clinicId, patientId);
      setInvoices(result || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load invoices', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [clinicId, patientId, toast]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleCreateNew = () => {
    setSelectedInvoice(null);
    setShowEditor(true);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setSelectedInvoice(null);
    fetchInvoices();
  };

  const handleVoid = async (invoiceId: string, reason?: string) => {
    if (!clinicId) return null;
    try {
      const updated = await invoicesService.void(clinicId, invoiceId, reason);
      toast({ title: 'Voided', description: 'Invoice has been voided' });
      fetchInvoices();
      return updated;
    } catch {
      toast({ title: 'Error', description: 'Failed to void invoice', variant: 'destructive' });
      return null;
    }
  };

  const handleDownloadPdf = async (invoiceId: string) => {
    if (!clinicId) return;
    try {
      const { pdfUrl } = await invoicesService.getPdf(clinicId, invoiceId);
      window.open(pdfUrl, '_blank');
    } catch {
      toast({ title: 'Error', description: 'Failed to generate PDF', variant: 'destructive' });
    }
  };

  if (showEditor && clinicId) {
    return (
      <InvoiceEditor
        clinicId={clinicId}
        invoice={selectedInvoice}
        defaultPatient={patient}
        onClose={handleEditorClose}
        onVoid={handleVoid}
        onDownloadPdf={handleDownloadPdf}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Invoices</h2>
          <p className="text-sm text-slate-500">
            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''} on file
          </p>
        </div>
        <Button
          onClick={handleCreateNew}
          className="bg-[#00A79D] hover:bg-[#008c85]"
          size="sm"
          disabled={!patientId}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Invoice
        </Button>
      </div>

      {invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 py-12">
          <Receipt className="h-10 w-10 text-slate-400 mb-3" />
          <p className="text-sm font-medium text-slate-600">No invoices yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Create an invoice to bill this patient for services or products
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={handleCreateNew}
            disabled={!patientId}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create First Invoice
          </Button>
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleViewInvoice(inv)}
                >
                  <TableCell className="font-medium">
                    {inv.invoice_number || 'DRAFT'}
                  </TableCell>
                  <TableCell>
                    {inv.issued_at
                      ? new Date(inv.issued_at).toLocaleDateString()
                      : new Date(inv.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={`capitalize ${INVOICE_STATUS_COLORS[inv.status] || 'bg-gray-100'}`}>
                      {inv.status.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="capitalize text-sm text-muted-foreground">
                    {inv.source.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCents(inv.total_cents)}
                  </TableCell>
                  <TableCell className="text-right">
                    {inv.balance_cents > 0 ? (
                      <span className="text-red-600 font-medium">{formatCents(inv.balance_cents)}</span>
                    ) : (
                      <span className="text-green-600">$0.00</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewInvoice(inv);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default PatientInvoicesTab;
